import { useState, useRef, useCallback } from 'react';
import { AUDIO_CONSTRAINTS } from '@/types/article';
import UploadIcon from '@/assets/common/upload.svg?react';
import TrashIcon from '@/assets/common/trash.svg?react';
import PlayIcon from '@/assets/common/play.svg?react';
import PauseIcon from '@/assets/common/pause.svg?react';
import styles from './AudioUploader.module.scss';

interface AudioUploaderProps {
  onFileSelect: (file: File, durationSeconds?: number) => void;
  disabled?: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function AudioUploader({ onFileSelect, disabled = false }: AudioUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [duration, setDuration] = useState<number | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!AUDIO_CONSTRAINTS.ALLOWED_MIME_TYPES.includes(file.type as any)) {
      return `Invalid file type. Supported formats: MP3, WAV, OGG, WebM, AAC, M4A`;
    }

    // Check file size
    if (file.size > AUDIO_CONSTRAINTS.MAX_FILE_SIZE_BYTES) {
      return `File too large. Maximum size is ${AUDIO_CONSTRAINTS.MAX_FILE_SIZE_BYTES / (1024 * 1024)}MB`;
    }

    return null;
  };

  const handleFile = useCallback((file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    setSelectedFile(file);

    // Create preview URL and detect duration
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    // Create temporary audio element to get duration
    const audio = new Audio(url);
    audio.addEventListener('loadedmetadata', () => {
      setDuration(audio.duration);
    });
  }, [previewUrl]);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play();
      setIsPlaying(true);
    }
  };

  const handleDiscard = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setSelectedFile(null);
    setPreviewUrl(null);
    setDuration(null);
    setIsPlaying(false);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUseFile = () => {
    if (selectedFile) {
      onFileSelect(selectedFile, duration || undefined);
    }
  };

  // Audio event handlers
  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const acceptedTypes = AUDIO_CONSTRAINTS.ALLOWED_MIME_TYPES.join(',');

  return (
    <div className={styles.uploader}>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedTypes}
        onChange={handleFileInputChange}
        className={styles.uploader__input}
        disabled={disabled}
      />

      {error && (
        <div className={styles.uploader__error}>{error}</div>
      )}

      {!selectedFile ? (
        <div
          className={`${styles.uploader__dropzone} ${isDragging ? styles['uploader__dropzone--active'] : ''} ${disabled ? styles['uploader__dropzone--disabled'] : ''}`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <UploadIcon className={styles.uploader__icon} />
          <p className={styles.uploader__text}>
            Drag and drop an audio file here, or{' '}
            <button
              type="button"
              className={styles.uploader__browseButton}
              onClick={handleBrowseClick}
              disabled={disabled}
            >
              browse
            </button>
          </p>
          <p className={styles.uploader__hint}>
            MP3, WAV, OGG, WebM, AAC, M4A (max {AUDIO_CONSTRAINTS.MAX_FILE_SIZE_BYTES / (1024 * 1024)}MB)
          </p>
        </div>
      ) : (
        <div className={styles.uploader__preview}>
          {previewUrl && (
            <audio
              ref={audioRef}
              src={previewUrl}
              onEnded={handleAudioEnded}
            />
          )}

          <div className={styles.uploader__previewControls}>
            <button
              className={styles.uploader__playButton}
              onClick={handlePlayPause}
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>

            <div className={styles.uploader__fileInfo}>
              <span className={styles.uploader__fileName}>{selectedFile.name}</span>
              <span className={styles.uploader__fileMeta}>
                {formatFileSize(selectedFile.size)}
                {duration && ` - ${formatTime(duration)}`}
              </span>
            </div>

            <button
              className={styles.uploader__discardButton}
              onClick={handleDiscard}
              title="Remove file"
            >
              <TrashIcon />
            </button>
          </div>

          <button
            className={styles.uploader__useButton}
            onClick={handleUseFile}
            disabled={disabled}
          >
            Use This File
          </button>
        </div>
      )}
    </div>
  );
}
